import { useState, useEffect } from "react";
import "@style/Admin.scss";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import { FaPlusSquare } from "react-icons/fa";
import ReportDefinitionModal from "@modals/ReportDefinitionModal";
import Format50ReportModal from "@modals/Format50ReportModal";

export default function AdminReportsTab(props: any) {
  if (!props || !props.tabData) {
    console.error('AdminReportsTab: props or props.tabData is undefined');
    return null;
  }
  
  const { updateSpinner, activeTab, adminPerms } = props.tabData;
  const [reportData, setReportData] = useState<any[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [editReportId, setEditReportId] = useState<number | null>(null);
  const [showFormat50Modal, setShowFormat50Modal] = useState<boolean>(false);

  useEffect(() => {
    if (isOnThisTab()) {
      getReportTypesAsync();
    }
  }, [activeTab]);

  if (!isOnThisTab()) return null;

  function isOnThisTab() {
    return activeTab === "reports";
  }

  async function getReportTypesAsync() {
    try {
      if (updateSpinner) updateSpinner(true);
      
      const resp = await RightTimeApi.getAsync("/ReportDefinitions");
      if (updateSpinner) updateSpinner(false);
      
      if (resp && resp.ok && Array.isArray(resp.jsonData)) {
        setReportData(resp.jsonData);
      } else {
        console.error("Failed to fetch report definitions:", resp?.statusText);
        toast.error("Failed to load report definitions");
      }
    } catch (error) {
      if (updateSpinner) updateSpinner(false);
      console.error("Error loading report definitions:", error);
      toast.error("Error loading report definitions");
    }
  }

  function onSelectReportType(e: any) {
    const reportId = parseInt(e.currentTarget.getAttribute("data-id"));
    setSelectedReportId(reportId);
    setEditReportId(reportId);
  }

  function onAddReportTypeAsync(e: any) {
    e.stopPropagation();
    setEditReportId(0);
  }

  function handleReportTypeModalClose(shouldRefresh) {
    setEditReportId(null);
    if (shouldRefresh) {
      getReportTypesAsync();
    }
  }

  // Always show the table structure, even if no data
  return (
    <div className={activeTab === "reports" ? "adminTabContent active" : "adminTabContent"}>
      <div className="admin-reports-container">
        <div className="reports-header">
          <h2>System Report</h2>
        </div>

        <div className="tableWrapper dimensionTable">
          <table className="horizontal">
            <caption>
              <span className="title">Report Definitions</span>
            </caption>
            <thead>
              <tr>
                <th>Id</th>
                <th className="wideNameHeader">Name</th>
                <th>Languages</th>
                <th>Active</th>
                <th onClick={onAddReportTypeAsync}>
                  <FaPlusSquare className="actionIcon" />
                </th>
              </tr>
            </thead>
            <tbody>
              {reportData && reportData.length > 0 ? (
                reportData.map((reportType) => {
                  return (
                    <tr
                      key={reportType.id}
                      className={selectedReportId === reportType.id ? "active" : ""}
                      data-id={reportType.id}
                      onClick={onSelectReportType}
                    >
                      <td>{reportType.id}</td>
                      <td>{reportType.name}</td>
                      <td>{Number(reportType.languageCount ?? reportType.LanguageCount) || 0}</td>
                      <td>{reportType.active ? "Yes" : "No"}</td>
                      <td className="actions"></td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="emptyStateCell">
                    No report definitions available. Click the + icon to add one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="tableWrapper dimensionTable">
          <table className="horizontal">
            <caption>
              <span className="title">Operational Reports</span>
            </caption>
            <thead>
              <tr>
                <th className="wideNameHeader">Name</th>
                <th>Description</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Completed Test Data (Format 50)</td>
                <td>Bulk export of completed tests for a company and date range as name/value TSV.</td>
                <td className="actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowFormat50Modal(true)}
                  >
                    Run
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <ReportDefinitionModal
        reportDefinitionId={editReportId}
        onClose={handleReportTypeModalClose}
        updateSpinner={updateSpinner}
      />

      <Format50ReportModal
        isOpen={showFormat50Modal}
        onClose={() => setShowFormat50Modal(false)}
        updateSpinner={updateSpinner}
      />
    </div>
  );
}
